/**
 * SolutionsHome — Grid of published solution landing pages
 * Routes: /solutions, /solutions/:locale
 */

import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { T } from '../styles/tokens';

interface SolutionCard {
  id: string; slug: string; title: string; excerpt: string;
  cover_image: string; metadata: string; custom_fields: string;
  features_json: string; view_count: number; sort_order: number;
}

const BASE = '/api/v1';

const gradients = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
];

export function SolutionsHome(props: { path: string; locale?: string }) {
  const [pages, setPages] = useState<SolutionCard[]>([]);
  const [loading, setLoading] = useState(true);
  const locale = props.locale || navigator.language || 'pt-BR';

  useEffect(() => {
    document.title = locale.startsWith('en') ? 'Solutions | WhiteLabelClaw' : 'Solucoes | WhiteLabelClaw';
    fetch(`${BASE}/cms/entries?contentType=solutions&status=published&locale=${encodeURIComponent(locale)}`)
      .then(r => r.json())
      .then((d: any) => { setPages(d.data?.entries || d.data?.pages || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [locale]);

  return (
    <div style={S.page}>
      <div style={S.hero}>
        <h1 style={S.heroTitle}>{locale.startsWith('en') ? 'Powerful Solutions for Every Need' : 'Solucoes Poderosas para Cada Necessidade'}</h1>
        <p style={S.heroSub}>{locale.startsWith('en') ? 'Discover all the tools that WhiteLabelClaw offers to accelerate your productivity' : 'Descubra todas as ferramentas que a WhiteLabelClaw oferece para acelerar sua produtividade'}</p>
      </div>

      {loading ? (
        <div style={{ padding: 60, display: 'flex', flexDirection: 'column', gap: 10 }}>{[0,1,2].map(i => <div key={i} style={{ height: 18, borderRadius: 6, background: `linear-gradient(90deg, ${T.borderSubtle} 25%, ${T.border} 50%, ${T.borderSubtle} 75%)`, backgroundSize: '200% 100%', animation: 'shimmer 1.5s ease-in-out infinite', animationDelay: `${i*0.1}s` }} />)}</div>
      ) : (
        <div style={S.grid}>
          {pages.map((p, i) => {
            const features: string[] = (() => { try { return JSON.parse(p.features_json || '[]'); } catch { return []; } })();
            const meta: any = (() => { try { return JSON.parse(p.metadata || '{}'); } catch { return {}; } })();
            const cf: any = (() => { try { return JSON.parse(p.custom_fields || '{}'); } catch { return {}; } })();
            return (
              <a key={p.id} href={`/solutions/${locale}/${p.slug}`} style={S.card}>
                <div style={{ ...S.cardImage, background: meta.hero_image ? `url(${meta.hero_image}) center/cover` : gradients[i % gradients.length] }} />
                <div style={S.cardBody}>
                  <h3 style={S.cardTitle}>{p.title || p.slug}</h3>
                  <p style={S.cardSub}>{p.excerpt || cf.description || ''}</p>
                  <div style={S.featureTags}>
                    {features.slice(0, 3).map((f, fi) => (
                      <span key={fi} style={S.tag}>{f}</span>
                    ))}
                  </div>
                  <div style={S.cardCta}>{cf.cta_text || 'Saiba mais'}</div>
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

const S = {
  page: { minHeight: '100vh', background: T.bg, color: T.text, fontFamily: "'Inter', -apple-system, sans-serif", padding: '0 20px 60px' } as any,
  hero: { textAlign: 'center' as const, padding: '60px 0 40px', maxWidth: 700, margin: '0 auto' },
  heroTitle: { fontSize: 36, fontWeight: 800, margin: '0 0 16px', background: 'linear-gradient(135deg, #5B8DEF, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' } as any,
  heroSub: { fontSize: 18, color: T.textSecondary, margin: 0, lineHeight: 1.6 } as any,
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 24, maxWidth: 1200, margin: '0 auto' } as any,
  card: { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 16, overflow: 'hidden', textDecoration: 'none', color: 'inherit', transition: 'transform 0.2s, border-color 0.2s, box-shadow 0.2s', display: 'block', cursor: 'pointer' } as any,
  cardImage: { height: 180, borderRadius: '16px 16px 0 0' } as any,
  cardBody: { padding: '20px 24px 24px' } as any,
  cardTitle: { fontSize: 18, fontWeight: 700, margin: '0 0 8px', color: T.text } as any,
  cardSub: { fontSize: 14, color: T.textSecondary, margin: '0 0 16px', lineHeight: 1.5 } as any,
  featureTags: { display: 'flex', flexWrap: 'wrap' as const, gap: 6, marginBottom: 16 },
  tag: { padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 500, background: T.primaryBg, color: T.primary } as any,
  cardCta: { display: 'inline-block', padding: '10px 24px', borderRadius: 8, fontSize: 14, fontWeight: 600, background: `linear-gradient(135deg, ${T.primary}, ${T.purple})`, color: T.text, textAlign: `center` as const, transition: 'opacity 0.15s' } as any,
};
